import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, Plus, Pencil, Trash2, Save, X, KanbanSquare, Link2, Target, Send, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { Card, Empty, Spinner } from "@/components/common";

const STATUS = { offen: "Offen", gesendet: "Gesendet", erhalten: "Erhalten", nachfassen: "Nachfassen", erledigt: "Erledigt" };
const CHANNEL = { email: "E-Mail", whatsapp: "WhatsApp", telefon: "Telefon", persoenlich: "Persönlich", sonstiges: "Sonstiges" };
const STATUS_TONE = {
  offen: "text-gray-300 border-white/15 bg-white/5",
  gesendet: "text-sky-300 border-sky-400/30 bg-sky-400/10",
  nachfassen: "text-amber-300 border-amber-400/30 bg-amber-400/10",
  erhalten: "text-[#A3E635] border-[#A3E635]/30 bg-[#A3E635]/10",
  erledigt: "text-[#A3E635] border-[#A3E635]/30 bg-[#A3E635]/10",
};
const inp = "w-full bg-black border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#A3E635]";
const empty = () => ({ contact_person: "", channel: "email", status: "offen", sent_at: "", followup_at: "", received_at: "", rating: "", review_text: "", internal_note: "", client_hint: "", show_in_report: false });

function Stat({ label, value, tone }) {
  return (
    <div className="bg-[#121217] border border-white/10 rounded-xl p-4">
      <div className="text-[11px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className={`mt-1 text-2xl font-black tracking-tight ${tone || "text-white"}`}>{value}</div>
    </div>
  );
}

export default function ReviewsTab({ clientId, readOnly = false }) {
  const [data, setData] = useState(null);
  const [link, setLink] = useState("");
  const [goal, setGoal] = useState(0);
  const [savingSet, setSavingSet] = useState(false);
  const [modal, setModal] = useState(null); // null | {} | action
  const [f, setF] = useState(empty());
  const [busy, setBusy] = useState(false);
  const [delId, setDelId] = useState(null);

  const load = () => api.get(`/clients/${clientId}/reviews`).then((r) => {
    setData(r.data);
    setLink(r.data.settings.google_review_link || "");
    setGoal(r.data.settings.monthly_goal || 0);
  });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);
  if (!data) return <Spinner />;

  const saveSettings = async () => {
    setSavingSet(true);
    try {
      await api.put(`/clients/${clientId}/reviews/settings`, { google_review_link: link, monthly_goal: Number(goal) || 0 });
      toast.success("Bewertungs-Einstellungen gespeichert"); load();
    } catch { toast.error("Speichern fehlgeschlagen"); } finally { setSavingSet(false); }
  };
  const openNew = () => { setF(empty()); setModal({}); };
  const openEdit = (a) => { setF({ ...empty(), ...a, rating: a.rating ?? "" }); setModal(a); };
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const saveAction = async () => {
    setBusy(true);
    const payload = { ...f, rating: f.rating === "" ? null : Number(f.rating) };
    try {
      if (modal && modal.id) await api.put(`/reviews/${modal.id}`, payload);
      else await api.post(`/clients/${clientId}/reviews`, payload);
      toast.success("Bewertungsaktion gespeichert"); setModal(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); } finally { setBusy(false); }
  };
  const remove = async (id) => {
    try { await api.delete(`/reviews/${id}`); toast.success("Bewertungsaktion gelöscht"); setDelId(null); load(); }
    catch { toast.error("Löschen fehlgeschlagen"); }
  };
  const toTask = async (id) => {
    try { await api.post(`/reviews/${id}/to-task`); toast.success("Aufgabe erstellt"); }
    catch { toast.error("Aufgabe konnte nicht erstellt werden"); }
  };
  const toggleReport = async (a) => {
    try { await api.put(`/reviews/${a.id}`, { show_in_report: !a.show_in_report }); load(); }
    catch { toast.error("Aktion fehlgeschlagen"); }
  };

  const s = data.stats;

  return (
    <div className="space-y-6" data-testid="detail-bewertungen">
      {/* Einstellungen */}
      <Card className="p-6" data-testid="reviews-settings">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-9 h-9 rounded-xl bg-[#A3E635]/10 flex items-center justify-center"><Star className="w-5 h-5 text-[#A3E635]" /></div>
          <div><h3 className="font-bold">Bewertungs-Booster</h3><p className="text-xs text-gray-500">Google-Bewertungslink und Monatsziel. Aktiv gepflegt — keine erfundenen Google-Daten.</p></div>
        </div>
        <fieldset disabled={readOnly} className="grid sm:grid-cols-[1fr_auto_auto] gap-3 items-end min-w-0">
          <label className="block">
            <span className="text-gray-500 text-xs uppercase tracking-wider flex items-center gap-1"><Link2 className="w-3 h-3" /> Google-Bewertungslink</span>
            <input className={`${inp} mt-1`} data-testid="reviews-link-input" placeholder="https://g.page/r/…" value={link} onChange={(e) => setLink(e.target.value)} />
          </label>
          <label className="block">
            <span className="text-gray-500 text-xs uppercase tracking-wider flex items-center gap-1"><Target className="w-3 h-3" /> Ziel / Monat</span>
            <input type="number" className={`${inp} mt-1 w-28`} data-testid="reviews-goal-input" value={goal} onChange={(e) => setGoal(e.target.value)} />
          </label>
          {!readOnly && <button onClick={saveSettings} disabled={savingSet} data-testid="reviews-settings-save"
            className="bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2 px-4 rounded-lg text-sm flex items-center gap-2 disabled:opacity-60">
            <Save className="w-4 h-4" /> Speichern
          </button>}
        </fieldset>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Ziel / Monat" value={s.goal || "—"} />
        <Stat label="Anfragen gesendet" value={s.sent} tone="text-sky-300" />
        <Stat label="Bewertungen erhalten" value={s.received} tone="text-[#A3E635]" />
        <Stat label="Offene Aktionen" value={s.open} tone={s.open > 0 ? "text-amber-300" : "text-white"} />
      </div>

      {/* Aktionen */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="font-bold">Bewertungsaktionen</h3>
        {!readOnly && <button onClick={openNew} data-testid="reviews-new-btn"
          className="bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2 px-4 rounded-lg text-sm flex items-center gap-2">
          <Plus className="w-4 h-4" /> Bewertungsaktion erstellen
        </button>}
      </div>

      {data.actions.length === 0 ? (
        <Empty text="Es sind noch keine echten Bewertungsdaten vorhanden. KEWAG kann den Bewertungslink hinterlegen und erste Bewertungsaktionen planen." />
      ) : (
        <Card className="divide-y divide-white/5">
          {data.actions.map((a) => (
            <div key={a.id} className="p-4 flex items-start justify-between gap-3" data-testid={`review-row-${a.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm">{a.contact_person || "Ohne Namen"}</span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${STATUS_TONE[a.status] || STATUS_TONE.offen}`}>{a.status_label}</span>
                  <span className="text-[10px] text-gray-500">{a.channel_label}</span>
                  {a.show_in_report && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border border-[#A3E635]/30 bg-[#A3E635]/10 text-[#A3E635]">Im Report</span>}
                </div>
                <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                  {a.sent_at && <span>Gesendet: {a.sent_at}</span>}
                  {a.followup_at && <span>Nachfassen: {a.followup_at}</span>}
                  {a.received_at && <span>Erhalten: {a.received_at}</span>}
                  {a.rating ? <span>{"★".repeat(a.rating)}</span> : null}
                </div>
                {a.review_text && <p className="text-xs text-gray-400 mt-1 italic">«{a.review_text}»</p>}
                {a.internal_note && <p className="text-[11px] text-amber-300/80 mt-1">Intern: {a.internal_note}</p>}
              </div>
              {!readOnly && <div className="flex items-center gap-1.5 shrink-0">
                <button onClick={() => toggleReport(a)} title="Im Monatsreport anzeigen" data-testid={`review-report-${a.id}`}
                  className={`p-2 rounded-lg border ${a.show_in_report ? "border-[#A3E635]/40 text-[#A3E635] bg-[#A3E635]/10" : "border-white/10 text-gray-400 hover:bg-white/5"}`}>
                  <CheckCircle2 className="w-4 h-4" />
                </button>
                <button onClick={() => toTask(a.id)} title="Als Aufgabe anlegen" data-testid={`review-totask-${a.id}`}
                  className="p-2 rounded-lg border border-white/10 text-gray-400 hover:bg-white/5"><KanbanSquare className="w-4 h-4" /></button>
                <button onClick={() => openEdit(a)} title="Bearbeiten" data-testid={`review-edit-${a.id}`}
                  className="p-2 rounded-lg border border-white/10 text-gray-400 hover:bg-white/5"><Pencil className="w-4 h-4" /></button>
                {delId === a.id ? (
                  <button onClick={() => remove(a.id)} data-testid={`review-confirm-del-${a.id}`} className="text-xs font-bold text-red-400 px-2">Löschen?</button>
                ) : (
                  <button onClick={() => setDelId(a.id)} title="Löschen" data-testid={`review-del-${a.id}`}
                    className="p-2 rounded-lg border border-white/10 text-red-400 hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></button>
                )}
              </div>}
            </div>
          ))}
        </Card>
      )}

      {/* Modal */}
      {modal !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="review-modal">
          <div className="bg-[#121217] border border-white/10 rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold">{modal.id ? "Bewertungsaktion bearbeiten" : "Bewertungsaktion erstellen"}</h3>
              <button onClick={() => setModal(null)} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className="text-xs text-gray-400 mb-1 block">Kontaktperson</label><input className={inp} data-testid="review-f-contact" value={f.contact_person} onChange={(e) => set("contact_person", e.target.value)} /></div>
              <div><label className="text-xs text-gray-400 mb-1 block">Anfragekanal</label>
                <select className={inp} data-testid="review-f-channel" value={f.channel} onChange={(e) => set("channel", e.target.value)}>
                  {Object.entries(CHANNEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select></div>
              <div><label className="text-xs text-gray-400 mb-1 block">Status</label>
                <select className={inp} data-testid="review-f-status" value={f.status} onChange={(e) => set("status", e.target.value)}>
                  {Object.entries(STATUS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select></div>
              <div><label className="text-xs text-gray-400 mb-1 block">Sternebewertung (1–5)</label><input type="number" min="1" max="5" className={inp} data-testid="review-f-rating" value={f.rating} onChange={(e) => set("rating", e.target.value)} /></div>
              <div><label className="text-xs text-gray-400 mb-1 block">Anfrage gesendet</label><input type="date" className={inp} data-testid="review-f-sent" value={(f.sent_at || "").slice(0,10)} onChange={(e) => set("sent_at", e.target.value)} /></div>
              <div><label className="text-xs text-gray-400 mb-1 block">Nachfassen am</label><input type="date" className={inp} data-testid="review-f-followup" value={(f.followup_at || "").slice(0,10)} onChange={(e) => set("followup_at", e.target.value)} /></div>
              <div><label className="text-xs text-gray-400 mb-1 block">Bewertung erhalten am</label><input type="date" className={inp} data-testid="review-f-received" value={(f.received_at || "").slice(0,10)} onChange={(e) => set("received_at", e.target.value)} /></div>
            </div>
            <div className="mt-3"><label className="text-xs text-gray-400 mb-1 block">Bewertungstext (falls manuell erfasst)</label><textarea className={`${inp} min-h-[60px]`} data-testid="review-f-text" value={f.review_text} onChange={(e) => set("review_text", e.target.value)} /></div>
            <div className="mt-3"><label className="text-xs text-gray-400 mb-1 block">Kundenhinweis (für Kunde sichtbar)</label><input className={inp} data-testid="review-f-hint" value={f.client_hint} onChange={(e) => set("client_hint", e.target.value)} /></div>
            <div className="mt-3"><label className="text-xs text-amber-300/80 mb-1 block">Interne Notiz (nur KEWAG — für Kunde unsichtbar)</label><textarea className={`${inp} min-h-[60px]`} data-testid="review-f-note" value={f.internal_note} onChange={(e) => set("internal_note", e.target.value)} /></div>
            <label className="mt-3 flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
              <input type="checkbox" data-testid="review-f-report" checked={!!f.show_in_report} onChange={(e) => set("show_in_report", e.target.checked)} /> Im Monatsreport anzeigen
            </label>
            <div className="flex items-center gap-3 mt-5">
              <button onClick={saveAction} disabled={busy} data-testid="review-f-save"
                className="bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2 px-4 rounded-lg text-sm flex items-center gap-2 disabled:opacity-60">
                <Save className="w-4 h-4" /> Speichern
              </button>
              <button onClick={() => setModal(null)} className="text-sm text-gray-400 hover:text-white px-3 py-2">Abbrechen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
